import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraEmptyState, SpecraIconButton, SpecraLink, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus, Unlink } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTablePreferences } from '../../../../hooks/useTablePreferences';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { EntityTable } from '../../../Delivery/_shared/components/EntityTable';
import { EmptyState } from '../../../Delivery/_shared/components/PageStates';
import { useCreateRfiResponseProject, useListRfiResponsesForCapture, useUnlinkRfiResponseFromCapture } from '../../../RfiResponse/hooks';
import { RFI_COLUMNS } from '../../CaptureDetailPage';
import { RfiSheet } from '../../entitySheets';
import { useCaptureWorkspace } from '../../hooks';
import { ConfirmRemoveButton } from '../ConfirmRemoveButton';
import { usePursuitRecord } from '../PursuitRecordContext';
import { PursuitSectionFrame } from '../PursuitSectionFrame';
const RFI_STATUS_OPTIONS = [
    {
        value: 'draft',
        label: 'Draft'
    },
    {
        value: 'in_progress',
        label: 'In progress'
    },
    {
        value: 'submitted',
        label: 'Submitted'
    },
    {
        value: 'closed',
        label: 'Closed'
    }
];
function rfiStatusLabel(status) {
    return RFI_STATUS_OPTIONS.find((option)=>option.value === status)?.label || status || 'Unknown';
}
/**
 * RFI responses (legacy `?tab=rfis`): the linked response workspaces and the
 * create button; the legacy tracked-RFI table sits under a collapsed
 * "Earlier RFI notes" group.
 */ export function RfiResponsesSection() {
    const { captureProjectId, capabilities, correlationId, header } = usePursuitRecord();
    const { data } = useCaptureWorkspace(captureProjectId);
    const { data: links } = useListRfiResponsesForCapture(captureProjectId);
    const create = useCreateRfiResponseProject();
    const unlink = useUnlinkRfiResponseFromCapture();
    const navigate = useNavigate();
    const prefs = useTablePreferences('ts:capture:rfis');
    const [showEarlier, setShowEarlier] = useState(false);
    const [sheet, setSheet] = useState({
        open: false,
        value: null
    });
    const workspaces = links?.workspaces ?? [];
    const legacy = links?.legacyRfiEvents ?? data?.rfiEvents ?? [];
    const name = header?.name ?? data?.summary?.name ?? '';
    return /*#__PURE__*/ _jsxs(PursuitSectionFrame, {
        actions: capabilities.canEdit ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.capture.addRfiButton,
                    iconLeft: /*#__PURE__*/ _jsx(Plus, {
                        className: "h-4 w-4"
                    }),
                    onClick: ()=>setSheet({
                            open: true,
                            value: null
                        }),
                    size: "sm",
                    variant: "outline",
                    children: "Log RFI"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.capture.createRfiResponseButton,
                    disabled: create.isPending,
                    iconLeft: /*#__PURE__*/ _jsx(Plus, {
                        className: "h-4 w-4"
                    }),
                    onClick: ()=>create.mutate({
                            title: name ? `RFI — ${name}` : 'RFI Response',
                            captureProjectId
                        }, {
                            onSuccess: (s)=>navigate(`/rfi-response/${s.zouProjectId || s.rfiResponseProjectId}`)
                        }),
                    size: "sm",
                    children: "Create RFI Response"
                })
            ]
        }) : undefined,
        title: "RFI responses",
        children: [
            workspaces.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                description: "Create a response workspace to draft an answer.",
                framed: true,
                title: "No RFI responses yet"
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "divide-y divide-border rounded-[var(--radius-card)] border border-border",
                children: workspaces.map((w)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-center justify-between gap-[var(--space-3)] px-[var(--space-3)] py-[var(--space-2)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraLink, {
                                className: "min-w-0 truncate font-medium",
                                "data-testid": Testids.capture.rfiWorkspaceRow,
                                href: `/rfi-response/${w.zouProjectId || w.rfiResponseProjectId}`,
                                renderLink: renderRouterLink,
                                children: w.title
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "flex shrink-0 items-center gap-[var(--space-2)]",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: rfiStatusLabel(w.status)
                                    }),
                                    capabilities.canEdit && /*#__PURE__*/ _jsx(ConfirmRemoveButton, {
                                        confirmLabel: "Unlink",
                                        description: "The RFI response stays; it is no longer linked to this pursuit.",
                                        onConfirm: ()=>unlink.mutate({
                                                captureProjectId,
                                                rfiResponseProjectId: w.rfiResponseProjectId
                                            }),
                                        title: `Unlink ${w.title}?`,
                                        trigger: /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            "data-testid": Testids.capture.unlinkRfiWorkspaceButton,
                                            disabled: unlink.isPending,
                                            label: "Unlink RFI Response workspace",
                                            size: "sm",
                                            variant: "ghost",
                                            children: /*#__PURE__*/ _jsx(Unlink, {
                                                className: "h-4 w-4"
                                            })
                                        })
                                    })
                                ]
                            })
                        ]
                    }, w.rfiResponseProjectId))
            }),
            legacy.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        action: /*#__PURE__*/ _jsx(SpecraButton, {
                            "aria-expanded": showEarlier,
                            onClick: ()=>setShowEarlier((v)=>!v),
                            size: "sm",
                            variant: "ghost",
                            children: showEarlier ? 'Hide' : `Show (${legacy.length})`
                        }),
                        title: "Earlier RFI notes",
                        variant: "group"
                    }),
                    showEarlier && /*#__PURE__*/ _jsx(EntityTable, {
                        columns: RFI_COLUMNS,
                        data: legacy,
                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                            description: "Try clearing the filters.",
                            title: "No RFIs match"
                        }),
                        filters: prefs.filters,
                        onFilterChange: prefs.setFilter,
                        onRowClick: (r)=>setSheet({
                                open: true,
                                value: r
                            }),
                        onToggleSort: prefs.toggleSort,
                        rowKey: (r)=>r.id,
                        rowTestid: Testids.capture.rfiRow,
                        sort: prefs.sort,
                        testid: Testids.capture.rfiTable
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(RfiSheet, {
                captureProjectId: captureProjectId,
                correlationId: correlationId,
                onOpenChange: (open)=>setSheet((s)=>({
                            ...s,
                            open
                        })),
                open: sheet.open,
                rfi: sheet.value
            })
        ]
    });
}
